import Vue from 'vue'
import deserialize from 'app/utils/deserialize'
import FormErrors from 'app/utils/form_errors'
import { fetch, update, remove, create } from 'app/utils/data'
import pickBy from 'lodash/pickBy'

function getRandomInt(min, max) {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min)) + min //The maximum is exclusive and the minimum is inclusive
}

function getStatusConstants() {
  const statuses = {
    success: 'success',
    failure: 'failure',
    submitting: 'submitting',
    editing: 'editing',
    pending: 'pending',
    showSubmitStatus: 'showSubmitStatus',
    lastSubmitStatus: 'lastSubmitStatus'
  }
  return Object.freeze ? Object.freeze(statuses) : statuses
}

function defaultSeatAssignmentStatuses() {
  const status = getStatusConstants()

  return {
    [status.editing]: false,
    [status.submitting]: false,
    [status.showSubmitStatus]: false,
    [status.pending]: false,
    [status.lastSubmitStatus]: status.success
  }
}

export function generateTempId() {
  return `tempId-${Date.now()}-${getRandomInt(0, 100000000)}` // integer milliseconds
}

export const status = getStatusConstants()

export default {
  namespaced: true,

  state: {
    items: null
  },

  status: getStatusConstants(),

  defaultSeatAssignmentStatuses: defaultSeatAssignmentStatuses(),

  mutations: {
    allSeatAssignmentsFetched(state, { seatAssignments }) {
      const fetchedItems = {}
      for (var i = 0; i < seatAssignments.length; i++) {
        fetchedItems[seatAssignments[i].id] = {
          ...seatAssignments[i],
          originalName: seatAssignments[i].name,
          ...defaultSeatAssignmentStatuses()
        }
      }

      // @TODO: what should we do about the case where, say, seatAssignment with ID 42
      // is currently submitting asynchronously, and here comes a refresh of item 42
      // which would overwrite both data and (submitting status) metadata?
      // This could happen if an update happened on the server while this client was
      // trying to submit an update--a race condition.
      // Or what about the case where the user is currently editing a seatAssignment
      // that is being overwritten by this mutation?
      state.items = fetchedItems
    },

    seatAssignmentFetched(state, { seatAssignment, tempId }) {
      const fetchedItem = {
        ...seatAssignment,
        originalName: seatAssignment.name,
        ...defaultSeatAssignmentStatuses()
      }

      // Add the new one
      Vue.set(state.items, seatAssignment.id, fetchedItem)

      if (tempId) {
        // Remove the pending one
        Vue.delete(state.items, tempId)
      }
    },

    seatAssignmentRemoved(state, { id }) {
      delete state.items[id]
    },

    seatAssignmentAllocated(state, { id, seatNumber }) {
      Vue.set(state.items, id, {
        id,
        name: '',
        ...defaultSeatAssignmentStatuses(),
        // initializing status as pending (not yet saved)
        [status.pending]: true,
        seatNumber
      })
    },

    seatAssignmentChanged(state, { name, id, seatNumber }) {
      state.items[id].name = name

      if (state.items[id].originalName !== name) {
        state.items[id][status.editing] = true
      }

      if (seatNumber) {
        state.items[id].seatNumber = seatNumber
      }
    },

    seatAssignmentStatusSet(state, { id, status, value }) {
      state.items[id][status] = value
    },

    seatAssignmentSubmitStatusUpdated(state, { id, status }) {
      state.items[id].lastSubmitStatus = status
    },

    resetState(state) {
      state.items = null
    }
  },

  getters: {
    seatAssignments(state) {
      return state.items || {}
    },

    savedSeatAssignments(state) {
      return () => pickBy(state.items, (value, key) => !value[status.pending])
    },

    pendingSeatAssignments(state) {
      return () => pickBy(state.items, (value, key) => value[status.pending])
    }
  },

  actions: {
    async fetchSeatAssignments({ commit, getters }) {
      try {
        const response = await fetch({
          url: '/api/account/seats',
          type: 'seats'
        })

        const seatAssignments = await deserialize(response.data)

        commit('allSeatAssignmentsFetched', { seatAssignments })

        return seatAssignments
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat' }])
        }
      }
    },

    changeSeatAssignment({ commit, getters }, { name, id, seatNumber }) {
      commit('seatAssignmentChanged', { name, id, seatNumber })
    },

    setSeatAssignmentStatus({ commit }, { id, status, value }) {
      commit('seatAssignmentStatusSet', { id, status, value })
    },

    async updateSeatAssignment({ commit, getters }, { id }) {
      const seatAssignment = getters.seatAssignments[id]
      const { success, failure, showSubmitStatus } = getStatusConstants()

      try {
        const response = await update({
          url: `/api/account/seats/${id}`,
          type: 'seats',
          changes: { name: seatAssignment.name },
          record: { name: seatAssignment.name }
        })

        const dataRecord = await deserialize(response.data)

        commit('seatAssignmentFetched', { seatAssignment: dataRecord })
        commit('seatAssignmentSubmitStatusUpdated', { id, status: success })
        commit('seatAssignmentStatusSet', { id, status: showSubmitStatus, value: true })
        setTimeout(() => {
          commit('seatAssignmentStatusSet', { id, status: showSubmitStatus, value: false })
        }, 2000)
        return dataRecord
      } catch (error) {
        commit('seatAssignmentSubmitStatusUpdated', { id, status: failure })
        commit('seatAssignmentStatusSet', { id, status: showSubmitStatus, value: true })
        if (error.response && error.response.data && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat' }])
        }
      }
    },

    async removeSeatAssignment({ commit, getters }, { id }) {
      try {
        await remove({
          url: `/api/account/seats/${id}`,
          type: 'seats'
        })

        commit('seatAssignmentRemoved', { id })

        return true
      } catch (error) {
        commit('seatAssignmentSubmitStatusUpdated', { id, status: status.failure })
        if (error.response && error.response.data && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat' }])
        }
      }
    },

    async createSeatAssignment({ commit, getters }, { id }) {
      const seatAssignment = getters.seatAssignments[id]
      const { success, failure, showSubmitStatus } = getStatusConstants()

      try {
        const response = await create({
          url: '/api/account/seats',
          type: 'seats',
          data: { name: seatAssignment.name }
        })

        const persistedRecord = await deserialize(response.data)

        commit('seatAssignmentFetched', { seatAssignment: persistedRecord, tempId: seatAssignment.id })
        commit('seatAssignmentSubmitStatusUpdated', { id: persistedRecord.id, status: success })
        commit('seatAssignmentStatusSet', { id: persistedRecord.id, status: showSubmitStatus, value: true })
        setTimeout(() => {
          commit('seatAssignmentStatusSet', { id: persistedRecord.id, status: showSubmitStatus, value: false })
        }, 2000)
        return persistedRecord
      } catch (error) {
        commit('seatAssignmentSubmitStatusUpdated', { id, status: failure })
        commit('seatAssignmentStatusSet', { id, status: showSubmitStatus, value: true })
        if (error.response && error.response.data && error.response.data.errors) {
          throw new FormErrors(error.response.data.errors)
        } else {
          throw new FormErrors([{ detail: 'PC LOAD LETTER: retry with a baseball bat' }])
        }
      }
    },

    allocateSeatAssignment({ commit }, { tempId, seatNumber }) {
      commit('seatAssignmentAllocated', { id: tempId, seatNumber })
    }
  }
}
